import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { UploadCloud, Sparkles, ArrowLeft, Loader2 } from "lucide-react";

const normSeller = (s) => (s || "").toUpperCase().replace(/[^A-Z0-9]+/g, " ").trim();

const matchParty = (name, list) => {
  const t = normSeller(name);
  if (!t) return { status: "empty", canonical: "", candidates: [] };
  const normed = list.map((p) => ({ p, n: normSeller(p) }));
  const exact = normed.find((x) => x.n === t);
  if (exact) return { status: "exact", canonical: exact.p, candidates: [exact.p] };
  const tset = new Set(t.split(" "));
  const cands = normed.filter((x) => {
    if (x.n.includes(t) || t.includes(x.n)) return true;
    const xset = new Set(x.n.split(" "));
    const inter = [...tset].filter((w) => xset.has(w)).length;
    const minlen = Math.min(tset.size, xset.size);
    return minlen > 0 && inter / minlen >= 0.6;
  }).map((x) => x.p);
  if (cands.length) return { status: "similar", canonical: cands[0], candidates: cands.slice(0, 6) };
  return { status: "none", canonical: "", candidates: [] };
};

const toISODate = (v) => {
  if (!v) return "";
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  let m = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/); // DD/MM/YYYY
  if (m) {
    let [, d, mo, y] = m;
    if (y.length === 2) y = "20" + y;
    return `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  const dt = new Date(s);
  if (!isNaN(dt.getTime())) {
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  }
  return "";
};

const FIELDS = [
  ["po_number", "Buyer P.O. Number", "text"],
  ["po_date", "P.O. Date", "date"],
  ["buyer_name", "Buyer (Garment Exporter)", "text"],
  ["seller_name", "Seller (Weaver / Mill)", "text"],
  ["category_seller", "Category Seller", "text"],
  ["fabric_type", "Fabric", "text"],
  ["quality_details", "Quality / Construction", "text"],
  ["quantity_meters", "Quantity (meters)", "number"],
  ["rate_per_meter", "Rate / meter (₹)", "number"],
  ["total_amount", "Amount (₹)", "number"],
  ["expected_delivery_date", "Delivery Date", "date"],
  ["payment_terms", "Payment Terms", "text"],
  ["payment_terms_days", "Credit Days", "number"],
];

const empty = {
  po_number: "", po_date: "", buyer_name: "", seller_name: "", category_seller: "", fabric_type: "",
  quality_details: "", quantity_meters: 0, rate_per_meter: 0, total_amount: 0,
  expected_delivery_date: "", payment_terms: "", payment_terms_days: 0, notes: "", po_image: "",
  commission_type: "percent", commission_value: 0,
};

export default function CreateOrder() {
  const navigate = useNavigate();
  const fileRef = useRef();
  const [scanning, setScanning] = useState(false);
  const [preview, setPreview] = useState("");
  const [form, setForm] = useState(empty);
  const [extracted, setExtracted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [buyers, setBuyers] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sellerMap, setSellerMap] = useState({});
  const [commissionRates, setCommissionRates] = useState({});

  useEffect(() => {
    api.get("/parties").then((r) => {
      setBuyers(r.data.filter((p) => p.role === "buyer").map((p) => p.name));
      setSellers((prev) => Array.from(new Set([...prev, ...r.data.filter((p) => p.role === "seller").map((p) => p.name)])));
    }).catch(() => {});
    api.get("/seller-categories").then((r) => {
      setCategories(r.data.category_list || []);
      setSellerMap(r.data.mapping || {});
      setSellers((prev) => Array.from(new Set([...prev, ...(r.data.seller_list || [])])));
    }).catch(() => {});
    api.get("/commission-rates").then((r) => setCommissionRates(r.data.rates || {})).catch(() => {});
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPreview(url);
    setScanning(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/extract/order", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const base = { ...empty, ...data };
      base.po_date = toISODate(data.po_date);
      base.expected_delivery_date = toISODate(data.expected_delivery_date);
      const bm = matchParty(data.buyer_name, buyers);
      if (bm.canonical) base.buyer_name = bm.canonical;
      const sm = matchParty(data.seller_name, sellers);
      if (sm.canonical) base.seller_name = sm.canonical;
      const pct = commissionRates[normSeller(base.seller_name)];
      if (pct !== undefined) { base.commission_type = "percent"; base.commission_value = pct; }
      setForm(base);
      setExtracted(true);
      toast.success("Details extracted — review & edit before saving.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Extraction failed. You can fill details manually.");
      setExtracted(true);
    } finally {
      setScanning(false);
    }
  };

  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (k === "quantity_meters" || k === "rate_per_meter") {
      next.total_amount = Math.round((Number(next.quantity_meters) || 0) * (Number(next.rate_per_meter) || 0) * 100) / 100;
    }
    if (k === "seller_name") {
      const cat = sellerMap[(v || "").trim().toLowerCase()];
      if (cat) next.category_seller = cat;
      const pct = commissionRates[normSeller(v)];
      if (pct !== undefined) { next.commission_type = "percent"; next.commission_value = pct; }
    }
    return next;
  });

  const commissionEstimate = form.commission_type === "per_meter"
    ? Math.round((Number(form.quantity_meters) || 0) * (Number(form.commission_value) || 0) * 100) / 100
    : Math.round((Number(form.total_amount) || 0) * (Number(form.commission_value) || 0) / 100 * 100) / 100;

  const addPartyInline = async (key) => {
    const role = key === "buyer_name" ? "buyer" : "seller";
    const name = (form[key] || "").trim();
    if (!name) return;
    try {
      await api.post("/parties", { name, role });
      if (role === "buyer") setBuyers((prev) => Array.from(new Set([...prev, name])));
      else setSellers((prev) => Array.from(new Set([...prev, name])));
      toast.success(`${name} added to Parties.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add party.");
    }
  };

  const save = async () => {
    if (!form.buyer_name && !form.seller_name && !form.po_number) {
      toast.error("Add at least a PO number, buyer or seller.");
      return;
    }
    const bm = matchParty(form.buyer_name, buyers);
    if (form.buyer_name && bm.status !== "exact") {
      toast.error(`Buyer "${form.buyer_name}" is not in Parties. Pick an existing party or add it under Parties first.`);
      return;
    }
    const sm = matchParty(form.seller_name, sellers);
    if (form.seller_name && sm.status !== "exact") {
      toast.error(`Seller "${form.seller_name}" is not in Parties. Pick an existing party or add it under Parties first.`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        quantity_meters: Number(form.quantity_meters) || 0,
        rate_per_meter: Number(form.rate_per_meter) || 0,
        total_amount: Number(form.total_amount) || 0,
        payment_terms_days: Number(form.payment_terms_days) || 0,
        commission_value: Number(form.commission_value) || 0,
      };
      const { data } = await api.post("/orders", payload);
      toast.success("Order created.");
      navigate(`/orders/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="create-order-page">
      <button onClick={() => navigate("/orders")} className="text-sm text-[#8CA3A7] flex items-center gap-1 hover:text-[#1C2331] transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to orders
      </button>
      <div>
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#8CA3A7]">New Indent</p>
        <h1 className="font-heading text-4xl font-bold text-[#0A0D14] tracking-tight mt-1">Create Order</h1>
        <p className="text-sm text-[#8CA3A7] mt-2">Upload a photo/screenshot of the Purchase Order. AI reads it, you review and save.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload / preview */}
        <div className="space-y-4">
          {!preview ? (
            <div
              data-testid="po-upload-zone"
              onClick={() => fileRef.current?.click()}
              className="relative overflow-hidden group border-2 border-dashed border-[#E6E4DD] bg-slate-50/50 hover:bg-slate-50 rounded-sm p-12 text-center transition-colors cursor-pointer flex flex-col items-center justify-center gap-4 min-h-[320px]"
            >
              <div className="w-14 h-14 bg-white border border-[#E6E4DD] rounded-sm flex items-center justify-center group-hover:border-[#1C2331] transition-colors">
                <UploadCloud className="w-6 h-6 text-[#D95D39]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#0A0D14]">Upload Purchase Order photo</p>
                <p className="text-xs text-[#8CA3A7] mt-1">PNG, JPG or screenshot · AI will extract the details</p>
              </div>
            </div>
          ) : (
            <div className="relative border border-[#E6E4DD] rounded-sm bg-white overflow-hidden">
              <div className="relative">
                <img src={preview} alt="PO preview" className="w-full max-h-[420px] object-contain bg-slate-50" />
                {scanning && <div className="scanline" />}
                {scanning && (
                  <div className="absolute inset-0 bg-white/40 flex items-center justify-center">
                    <div className="bg-[#1C2331] text-white text-xs font-mono px-3 py-1.5 rounded-sm flex items-center gap-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Scanning document…
                    </div>
                  </div>
                )}
              </div>
              <button
                data-testid="change-po-image"
                onClick={() => fileRef.current?.click()}
                className="w-full text-sm py-2.5 border-t border-[#E6E4DD] text-[#4B5563] hover:bg-slate-50 transition-colors"
              >
                Change image
              </button>
            </div>
          )}
          <input ref={fileRef} data-testid="po-file-input" type="file" accept="image/*" className="hidden" onChange={onFile} />
        </div>

        {/* Review form */}
        <div className="bg-white border border-[#E6E4DD] rounded-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <Sparkles className="w-4 h-4 text-[#D95D39]" />
            <h2 className="font-heading text-lg font-semibold text-[#0A0D14]">Review details</h2>
          </div>
          {!extracted && !scanning && (
            <p className="text-sm text-[#8CA3A7] mb-4">Upload a PO to auto-fill, or type in the details manually below.</p>
          )}
          <div className="grid grid-cols-2 gap-4">
            {FIELDS.map(([key, label, type]) => {
              const pm = key === "buyer_name" ? matchParty(form.buyer_name, buyers)
                : key === "seller_name" ? matchParty(form.seller_name, sellers) : null;
              return (
              <div key={key} className={key === "payment_terms" || key === "quality_details" ? "col-span-2" : ""}>
                <label className="text-xs font-medium uppercase tracking-[0.08em] text-[#8CA3A7]">{label}</label>
                <input
                  data-testid={`field-${key}`}
                  type={type}
                  list={key === "buyer_name" ? "buyers-list" : key === "seller_name" ? "sellers-list" : key === "category_seller" ? "categories-list" : undefined}
                  value={form[key]}
                  onChange={(e) => set(key, e.target.value)}
                  className="mt-1 w-full rounded-sm border border-[#E6E4DD] bg-white px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331] focus:border-[#1C2331] transition-colors"
                />
                {pm && pm.status === "exact" && (
                  <p data-testid={`party-status-${key}`} className="mt-1 text-[11px] text-[#065F46]">✓ Matched to Parties</p>
                )}
                {pm && pm.status === "similar" && (
                  <div data-testid={`party-status-${key}`} className="mt-1 text-[11px] text-[#92400E]">
                    Pick the existing party:
                    <div className="flex flex-wrap gap-1 mt-1">
                      {pm.candidates.map((c) => (
                        <button key={c} type="button" onClick={() => set(key, c)} className="px-2 py-0.5 rounded-sm border border-[#F59E0B]/50 bg-[#FFFBEB] hover:bg-[#FEF3C7] transition-colors">{c}</button>
                      ))}
                    </div>
                  </div>
                )}
                {pm && pm.status === "none" && (
                  <div data-testid={`party-status-${key}`} className="mt-1 text-[11px] text-[#991B1B] flex items-center gap-2 flex-wrap">
                    <span>Not in Parties.</span>
                    <button type="button" data-testid={`add-party-${key}`} onClick={() => addPartyInline(key)} className="px-2 py-0.5 rounded-sm border border-[#065F46]/40 bg-[#ECFDF5] text-[#065F46] font-medium hover:bg-[#D1FAE5] transition-colors">+ Add "{form[key]}" as new {key === "buyer_name" ? "buyer" : "seller"}</button>
                  </div>
                )}
              </div>
              );
            })}
            <datalist id="buyers-list">{buyers.map((b) => <option key={b} value={b} />)}</datalist>
            <datalist id="sellers-list">{sellers.map((s) => <option key={s} value={s} />)}</datalist>
            <datalist id="categories-list">{categories.map((c) => <option key={c} value={c} />)}</datalist>

            <div className="col-span-2 grid grid-cols-2 gap-4 border-t border-[#E6E4DD] pt-4 mt-1">
              <div>
                <label className="text-xs font-medium uppercase tracking-[0.08em] text-[#8CA3A7]">Commission Type</label>
                <select
                  data-testid="field-commission_type"
                  value={form.commission_type}
                  onChange={(e) => set("commission_type", e.target.value)}
                  className="mt-1 w-full rounded-sm border border-[#E6E4DD] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]"
                >
                  <option value="percent">% of order value</option>
                  <option value="per_meter">₹ per meter</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium uppercase tracking-[0.08em] text-[#8CA3A7]">{form.commission_type === "per_meter" ? "Commission ₹/meter" : "Commission %"}</label>
                <input
                  data-testid="field-commission_value"
                  type="number"
                  value={form.commission_value}
                  onChange={(e) => set("commission_value", e.target.value)}
                  className="mt-1 w-full rounded-sm border border-[#E6E4DD] bg-white px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#1C2331]"
                />
              </div>
              <div className="col-span-2 text-xs text-[#065F46] font-mono" data-testid="commission-estimate">
                Est. commission: ₹{commissionEstimate.toLocaleString("en-IN")}
              </div>
            </div>

            <div className="col-span-2">
              <label className="text-xs font-medium uppercase tracking-[0.08em] text-[#8CA3A7]">Notes</label>
              <textarea
                data-testid="field-notes"
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                rows={2}
                className="mt-1 w-full rounded-sm border border-[#E6E4DD] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331] focus:border-[#1C2331]"
              />
            </div>
          </div>

          <button
            data-testid="save-order-btn"
            onClick={save}
            disabled={saving}
            className="mt-6 w-full bg-[#1C2331] text-white rounded-sm px-4 py-2.5 text-sm font-medium transition-transform active:scale-[0.98] hover:bg-[#2A3447] disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save Order"}
          </button>
        </div>
      </div>
    </div>
  );
}
